import {
  WarningCircle, CheckCircle, Buildings, GraduationCap, Shield,
  Briefcase, Heart, HandHeart, Calculator, UsersThree, Compass,
} from '@phosphor-icons/react';

// ---------- 2. Why Automation and AI Projects Often Fail ----------

const COMMON_ISSUES = [
  'Work depends too heavily on specific individuals',
  'The same task is performed differently by different staff',
  'Information is repeatedly entered into multiple systems',
  'Reports take too long to prepare',
  'Errors are detected late, after they have moved downstream',
  'Staff spend excessive time on repetitive manual work',
  'AI tools are introduced without clear governance',
  'Data is fragmented across documents, spreadsheets, and systems',
  'Previous digital projects fail to deliver expected value',
];

export const ProblemSection = () => (
  <section id="problem" className="py-20 lg:py-28 bg-white" data-testid="problem-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-12">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">
          The Real Risk
        </div>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-5">
          Why Automation and AI Projects Often Fail
        </h2>
        <p className="text-slate-600 leading-relaxed text-base">
          Organisations often invest in new systems, automation tools, and AI solutions
          before understanding whether their workflows are ready to support transformation.
          When workflows are unstable, technology may accelerate existing problems rather
          than solve them.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
        {COMMON_ISSUES.map(issue => (
          <div
            key={issue}
            className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg px-5 py-4"
          >
            <WarningCircle size={20} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
            <span className="text-slate-700 text-[15px] leading-snug">{issue}</span>
          </div>
        ))}
      </div>
      <div className="border-l-4 border-teal-600 pl-5 py-2 bg-teal-50/40">
        <p className="text-slate-800 leading-relaxed font-medium">
          Before investing further in automation or AI, organisations need to understand whether
          their workflows are stable enough to support transformation.
        </p>
      </div>
    </div>
  </section>
);

// ---------- 3. Common Warning Signs ----------

const WARNING_SIGNS = [
  'Work stops when a key employee is absent',
  'Different staff perform the same process differently',
  'Managers rely on informal updates instead of reliable dashboards',
  'Reports take days or weeks to prepare',
  'Data has to be copied manually between systems',
  'Staff spend too much time on repetitive administrative tasks',
  'Errors are only discovered after work has moved downstream',
  'New staff take too long to understand how work is done',
  'AI tools are being used informally without guidance or policy',
  'Digital transformation initiatives have not delivered expected value',
];

export const WarningSignsSection = () => (
  <section className="py-20 lg:py-28 bg-slate-50" data-testid="warning-signs-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-12">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">
          Self-Check
        </div>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-4">
          Common Signs That Your Workflow May Not Be Ready for Automation
        </h2>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {WARNING_SIGNS.map(sign => (
          <li key={sign} className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg px-5 py-4 hover:border-teal-400 transition-colors">
            <CheckCircle size={20} weight="duotone" className="text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-slate-700 text-[15px] leading-snug">{sign}</span>
          </li>
        ))}
      </ul>
      <p className="text-slate-700 leading-relaxed mt-10 max-w-3xl text-[15px]">
        If several of these situations sound familiar, your organisation may benefit from a workflow
        stability and AI readiness assessment before committing further resources to automation or AI.
      </p>
    </div>
  </section>
);

// ---------- 4. What AWSAI Assesses ----------

const ASSESSED_DOMAINS = [
  { n: '1', title: 'Decision Dependency',
    desc: 'Whether critical work depends too heavily on specific individuals or undocumented knowledge.' },
  { n: '2', title: 'Process Consistency',
    desc: 'Whether the same workflow is performed consistently across staff, teams, and situations.' },
  { n: '3', title: 'Repetitive Work',
    desc: 'Whether staff time is being absorbed by routine manual activities that may be redesigned or automated.' },
  { n: '4', title: 'Error Control',
    desc: 'Whether errors are prevented at source or corrected later through rework.' },
  { n: '5', title: 'Data Readiness',
    desc: 'Whether organisational data is sufficiently structured, accessible, and reliable for automation and AI use.' },
  { n: '6', title: 'System Support',
    desc: 'Whether existing systems support daily workflows effectively or create fragmentation.' },
  { n: '7', title: 'Staff Capability',
    desc: 'Whether staff have the skills and confidence to work with digital tools, automation, and AI.' },
  { n: '8', title: 'AI Governance',
    desc: 'Whether the organisation has appropriate guidance, accountability, and controls for responsible AI use.' },
];

export const FrameworkSection = () => (
  <section id="framework" className="py-20 lg:py-28 bg-white" data-testid="framework-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-12">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">
          The AWSAI Framework
        </div>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-5">
          What AWSAI Assesses
        </h2>
        <p className="text-slate-600 leading-relaxed text-base mb-3">
          AWSAI provides a structured diagnostic assessment of workflow stability, process maturity,
          and organisational readiness for automation and AI-enabled transformation.
        </p>
        <p className="text-slate-600 leading-relaxed text-base">
          The assessment examines eight areas across operational, governance, and readiness dimensions:
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {ASSESSED_DOMAINS.map(d => (
          <div
            key={d.n}
            data-testid={`assessed-domain-${d.n}`}
            className="bg-gradient-to-b from-white to-slate-50 border border-slate-200 rounded-lg p-6 hover:border-teal-500 hover:shadow-md transition-all"
          >
            <div className="text-[11px] font-bold text-teal-700 tracking-[0.2em] mb-1">DOMAIN {d.n}</div>
            <div className="text-base font-bold text-slate-900 mb-2">{d.title}</div>
            <p className="text-sm text-slate-600 leading-relaxed">{d.desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 p-5 bg-slate-50 rounded-lg border-l-4 border-teal-600">
        <p className="text-slate-700 text-[15px] leading-relaxed">
          <strong className="text-slate-900">Applicable beyond accounting.</strong>{' '}
          Although AWSAI originated from workflow assessment research in accounting and professional
          service environments, it can be applied across operational, administrative, professional,
          academic, customer service, HR, procurement, finance, and support workflows.
        </p>
      </div>
    </div>
  </section>
);

// ---------- 5. Who Should Use AWSAI ----------

const AUDIENCE = [
  { icon: Briefcase, title: 'SMEs', desc: 'Improving workflow efficiency before scaling.' },
  { icon: GraduationCap, title: 'Universities & Academic Units', desc: 'Managing administrative and operational processes.' },
  { icon: Shield, title: 'Government Agencies', desc: 'Improving service delivery and operational accountability.' },
  { icon: Buildings, title: 'Professional Services Firms', desc: 'Managing client engagement and delivery workflows.' },
  { icon: Heart, title: 'Healthcare Organisations', desc: 'Improving operational workflows around patient services.' },
  { icon: HandHeart, title: 'NGOs & Social Enterprises', desc: 'Managing limited resources and reporting obligations.' },
  { icon: Calculator, title: 'Finance & Accounting Teams', desc: 'Strengthening month-end, reporting, and compliance flows.' },
  { icon: UsersThree, title: 'HR · Procurement · Admin · CS', desc: 'Standardising day-to-day cross-functional processes.' },
  { icon: Compass, title: 'Operations & Project Teams', desc: 'Building consistency before automating execution.' },
];

export const AudienceSection = () => (
  <section className="py-20 lg:py-28 bg-slate-50" data-testid="audience-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-12">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">Applicability</div>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-4">
          Who Should Use AWSAI?
        </h2>
        <p className="text-slate-600 leading-relaxed text-base">
          AWSAI is suitable for organisations and teams that want to understand whether their workflows
          are ready for improvement, automation, or AI-enabled transformation.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {AUDIENCE.map(a => (
          <div key={a.title} className="bg-white border border-slate-200 rounded-lg p-6 hover:border-teal-500 hover:shadow-md transition-all">
            <div className="w-11 h-11 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center mb-4">
              <a.icon size={22} weight="duotone" className="text-teal-700" />
            </div>
            <div className="text-base font-bold text-slate-900 mb-1">{a.title}</div>
            <p className="text-sm text-slate-600 leading-relaxed">{a.desc}</p>
          </div>
        ))}
      </div>
      <p className="text-slate-700 leading-relaxed mt-10 max-w-3xl text-[15px]">
        If your organisation is considering automation, AI adoption, process improvement, or digital
        transformation, AWSAI provides a practical starting point.
      </p>
    </div>
  </section>
);
